import type * as Market from 'models.market';
import type { AxiosError } from 'axios';
import { action, computed, thunk } from 'easy-peasy';
import type { Action, Computed, Thunk } from 'easy-peasy';
import { api } from '~/api';

export interface MarketdepthModel {
  renderCounter: number;
  orderQuestions: Record<string, Market.Question[]>;
  orderQuestionsCount: Computed<MarketdepthModel, (id: string) => number>;
  incRenderCounter: Action<MarketdepthModel>;
  setOrderQuestions: Action<
    MarketdepthModel,
    { id: string; questions: Market.Question[] }
  >;
  loadOrderQuestions: Thunk<MarketdepthModel, string>;
  loadTenderOrderQuestions: Thunk<
    MarketdepthModel,
    { tenderId: string; orderId: string }
  >;
}

const isForbidden = (err: unknown) =>
  (err as AxiosError)?.response?.status === 403;

const sortByCreated = (questions: Market.Question[]) =>
  [...questions].sort((a, b) =>
    a.created < b.created ? -1 : a.created > b.created ? 1 : 0
  );

const marketdepthModel: MarketdepthModel = {
  renderCounter: 0,
  orderQuestions: {},

  orderQuestionsCount: computed(
    (state) => (id) => (state.orderQuestions[id] ?? []).length
  ),

  incRenderCounter: action((state) => {
    state.renderCounter += 1;
  }),

  setOrderQuestions: action((state, payload) => {
    state.orderQuestions[payload.id] = payload.questions;
  }),

  loadOrderQuestions: thunk(async (actions, id) => {
    try {
      const data = await api.getData({
        type: 'orders',
        id: `${id}/questions`,
        getResult: (x) => x.questions,
      });
      actions.setOrderQuestions({ id, questions: sortByCreated(data) });
    } catch (err) {
      // 403: the viewer has lost access to the order, whose own GET is
      // refused as well, so drop questions cached while they still had it
      if (!isForbidden(err)) throw err;
      actions.setOrderQuestions({ id, questions: [] });
    }
  }),

  loadTenderOrderQuestions: thunk(async (actions, { tenderId, orderId }) => {
    try {
      const data = await api.getData({
        type: 'tenders',
        id: `${tenderId}/offers/${orderId}/questions`,
        getResult: (x) => x.questions,
      });
      actions.setOrderQuestions({
        id: orderId,
        questions: sortByCreated(data),
      });
    } catch (err) {
      // 403: viewer lacks product/division permission on this offer (FRAICHE-2Q)
      if (!isForbidden(err)) throw err;
    }
  }),
};

export default marketdepthModel;
